"use client";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";

export default function EligibilityCriteriaPage() {
  const navigate = useNavigate();
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const goBack = () => {
    navigate("/");
  };

  return (
    <div className="container mx-auto px-4 py-6 md:py-4">
      <button
        onClick={goBack}
        className="mb-4 flex items-center gap-3 text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg py-3 px-6 shadow-md hover:shadow-lg"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-6 w-6"
        >
          <path d="m12 19-7-7 7-7" />
          <path d="M19 12H5" />
        </svg>
        <span className="font-medium text-lg">Go Back</span>
      </button>

      <motion.div
        className="bg-white shadow-lg rounded-lg overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: inView ? 1 : 0 }}
        transition={{ duration: 1 }}
        ref={ref}
      >
        <div className="bg-[#1e3a8a] text-white p-4 md:p-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col md:flex-row items-center md:space-x-4 text-center md:text-left">
              <img
                src="/logo.png"
                alt="NASTP Logo"
                width={80}
                height={80}
                className="rounded-full mb-2 md:mb-0"
              />
              <div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight">
                  NASTP INSTITUTE OF INFORMATION
                </h1>
                <h2 className="text-lg md:text-xl lg:text-2xl font-bold tracking-tight">
                  TECHNOLOGY (NIIT), LAHORE
                </h2>
              </div>
            </div>
            <img
              src="/air-logo-transpatrent.png"
              alt="Air University Logo"
              width={100}
              height={100}
              className="mt-2 md:mt-0"
            />
          </div>
        </div>

        <div className="p-4 md:p-6">
          <div className="bg-gray-100 py-6 px-4 md:py-8 md:px-6">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-gray-800">
              Admission Eligibility Criteria
            </h2>
          </div>

          {/* Academic Qualifications Section */}
          <div className="space-y-6 md:space-y-8 mt-4 md:mt-6">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-blue-700 mb-3 md:mb-4">
                Academic Qualifications
              </h3>
              <div className="bg-blue-50 rounded-lg p-4 md:p-6 shadow-sm">
                <p className="text-base md:text-lg">
                  Candidates must have completed at least 12 years of education
                  (Intermediate or equivalent) with the following subject
                  requirements:
                </p>
                <ul className="space-y-3 md:space-y-4 mt-3 md:mt-4">
                  <li className="flex items-start">
                    <motion.div
                      className="h-5 w-5 md:h-6 md:w-6 text-green-600 mr-2 mt-1 flex-shrink-0"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: inView ? 1 : 0 }}
                      transition={{ duration: 1 }}
                    >
                      ✅
                    </motion.div>
                    <span className="text-base md:text-lg">
                      Intermediate / DAE / A-level (or equivalent) with
                      Mathematics as a subject
                      <br />➤ A minimum of 50% marks is required for eligibility
                      in all BS/BSc Computing programs.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <motion.div
                      className="h-5 w-5 md:h-6 md:w-6 text-green-600 mr-2 mt-1 flex-shrink-0"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: inView ? 1 : 0 }}
                      transition={{ duration: 1 }}
                    >
                      ✅
                    </motion.div>
                    <span className="text-base md:text-lg">
                      Intermediate Pre-Medical (Physics, Chemistry, Biology) or
                      equivalent
                      <br />➤ A minimum of 50% marks is required.
                      <br />➤ Candidates admitted through this stream must study
                      two additional Mathematics courses (6 credit hours) during
                      the first year of their degree at the institution.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Equivalence Certificate Section */}
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-blue-700 mb-3 md:mb-4">
                Equivalence Certificate
              </h3>
              <div className="bg-blue-50 rounded-lg p-4 md:p-6 shadow-sm">
                <p className="text-base md:text-lg">
                  Candidates holding qualifications other than those issued by
                  Pakistani boards must provide an equivalence certificate
                  issued by IBCC (Inter Board Committee of Chairmen).
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mt-6 md:mt-8 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4 text-gray-700">
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 md:h-6 md:w-6 text-blue-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span className="text-base md:text-lg">
                69-Abid Majeed Road, Lahore Cantt
              </span>
            </div>
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 md:h-6 md:w-6 text-blue-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <span className="text-base md:text-lg">0320-9420163</span>
            </div>
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 md:h-6 md:w-6 text-blue-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <span className="text-base md:text-lg">042-36666033</span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
